import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabaseAuthService } from '../../services/supabaseAuthService';
import { AuthContextType, User, LoginRequest, RegisterRequest, PasswordResetConfirm, UserPreferencesUpdate, ConsentUpdateRequest, PasswordStrengthResponse } from '../../types/auth';

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize authentication on app load
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        console.log('Initializing auth...');
        
        // Wait for Supabase auth to initialize and restore session
        const isAuthenticated = await supabaseAuthService.restoreAuthState();
        
        if (mounted) {
          if (isAuthenticated) {
            const currentUser = await supabaseAuthService.getCurrentUser();
            setUser(currentUser);
            console.log('Auth restored for user:', currentUser?.email);
          } else {
            console.log('No existing auth session found');
          }
          
          setIsInitialized(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setIsInitialized(true);
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // Create context value with auth methods
  const contextValue: AuthContextType = {
    user,
    tokens: null, // TODO: Implement token management
    isAuthenticated: !!user && supabaseAuthService.isAuthenticated(),
    isLoading,
    isInitialized,
    error: null, // TODO: Implement error state management
    
    login: async (credentials: LoginRequest): Promise<void> => {
      try {
        const response = await supabaseAuthService.login(credentials);
        setUser(response.user);
      } catch (error) {
        console.error('Login error:', error);
        throw error;
      }
    },

    register: async (userData: RegisterRequest): Promise<void> => {
      try {
        const response = await supabaseAuthService.register(userData);
        setUser(response.user);
      } catch (error) {
        console.error('Registration error:', error);
        throw error;
      }
    },

    logout: async (): Promise<void> => {
      try {
        await supabaseAuthService.logout();
        setUser(null);
      } catch (error) {
        console.error('Logout error:', error);
        // Clear local state even if logout fails
        setUser(null);
      }
    },

    refreshToken: async (): Promise<void> => {
      try {
        await supabaseAuthService.refreshAccessToken();
        const currentUser = await supabaseAuthService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Token refresh error:', error);
        // On refresh failure, logout user
        setUser(null);
        throw error;
      }
    },

    resetPassword: async (email: string): Promise<void> => {
      try {
        await supabaseAuthService.requestPasswordReset(email);
      } catch (error) {
        console.error('Password reset error:', error);
        throw error;
      }
    },

    confirmPasswordReset: async (data: PasswordResetConfirm): Promise<void> => {
      try {
        await supabaseAuthService.updatePassword(data.newPassword);
      } catch (error) {
        console.error('Password reset confirmation error:', error);
        throw error;
      }
    },

    verifyEmail: async (token: string): Promise<void> => {
      // TODO: Implement email verification with token
      console.log('Email verification not yet implemented:', token);
      throw new Error('Email verification not yet implemented');
    },

    updateProfile: async (preferences: UserPreferencesUpdate): Promise<void> => {
      // TODO: Implement profile update functionality
      console.log('Profile update not yet implemented:', preferences);
      throw new Error('Profile update not yet implemented');
    },

    updateConsent: async (consent: ConsentUpdateRequest): Promise<void> => {
      // TODO: Implement consent update functionality
      console.log('Consent update not yet implemented:', consent);
      throw new Error('Consent update not yet implemented');
    },

    checkPasswordStrength: async (password: string): Promise<PasswordStrengthResponse> => {
      // Simple password strength check implementation
      const minLength = 8;
      const hasUpper = /[A-Z]/.test(password);
      const hasLower = /[a-z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      
      const requirements = {
        minLength: password.length >= minLength,
        hasUppercase: hasUpper,
        hasLowercase: hasLower,
        hasNumber: hasNumber,
        hasSpecialChar: hasSpecial
      };
      
      const score = Object.values(requirements).reduce((acc, req) => acc + (req ? 1 : 0), 0);
      const isStrong = score >= 4 && requirements.minLength;
      
      const feedback: string[] = [];
      if (!requirements.minLength) feedback.push(`Password must be at least ${minLength} characters long`);
      if (!requirements.hasUppercase) feedback.push('Password must contain at least one uppercase letter');
      if (!requirements.hasLowercase) feedback.push('Password must contain at least one lowercase letter');
      if (!requirements.hasNumber) feedback.push('Password must contain at least one number');
      if (!requirements.hasSpecialChar) feedback.push('Password must contain at least one special character');
      
      return {
        isStrong,
        score,
        feedback,
        requirementsMet: requirements
      };
    },

    clearError: (): void => {
      // TODO: Implement error clearing
    },

    setLoading: (loading: boolean): void => {
      setIsLoading(loading);
    },

    initializeAuth: async (): Promise<void> => {
      try {
        const isAuthenticated = await supabaseAuthService.restoreAuthState();
        if (isAuthenticated) {
          const currentUser = await supabaseAuthService.getCurrentUser();
          setUser(currentUser);
        }
        setIsInitialized(true);
        setIsLoading(false);
      } catch (error) {
        console.error('Auth initialization error:', error);
        setIsInitialized(true);
        setIsLoading(false);
      }
    },
    
    requireAuth: () => {
      if (!user || !supabaseAuthService.isAuthenticated()) {
        throw new Error('Authentication required');
      }
    },

    hasRole: (role: User['role']) => {
      if (!user) return false;
      
      const roleHierarchy = { user: 1, premium: 2, admin: 3 };
      const userLevel = roleHierarchy[user.role] || 0;
      const requiredLevel = roleHierarchy[role] || 0;
      
      return userLevel >= requiredLevel;
    },

    hasPermission: (permission: string) => {
      if (!user) return false;
      
      // Define role-based permissions
      const rolePermissions = {
        user: ['read_telemetry', 'read_races'],
        premium: ['read_telemetry', 'read_races', 'advanced_analytics', 'export_data'],
        admin: ['read_telemetry', 'read_races', 'advanced_analytics', 'export_data', 'user_management', 'system_admin']
      };
      
      const userPermissions = rolePermissions[user.role] || [];
      return userPermissions.includes(permission);
    }
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  
  return context;
};

// Higher-order component for components that require authentication
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>,
  requiredRole?: User['role']
) => {
  const WrappedComponent: React.FC<P> = (props) => {
    const { isAuthenticated, user, hasRole } = useAuthContext();

    if (!isAuthenticated || !user) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
            <p className="text-gray-600">Please sign in to access this page.</p>
          </div>
        </div>
      );
    }

    if (requiredRole && !hasRole(requiredRole)) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };

  WrappedComponent.displayName = `withAuth(${Component.displayName || Component.name})`;
  return WrappedComponent;
};